import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Button, Space, message, Typography, Modal,
  Input, Form, Tabs, Upload, Image,
  Avatar, Pagination, Switch, Tag
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { workflowAPI } from '../services/api';
import { 
  EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
  CopyOutlined, UploadOutlined, SearchOutlined,
  CameraOutlined, EyeOutlined
} from '@ant-design/icons';
import WorkflowUploader from './WorkflowUploader';
import WorkflowBatchUploader from './WorkflowBatchUploader';

const { Title, Text } = Typography;
const { confirm } = Modal;
const { Search } = Input;

const WorkflowList = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [coverModalVisible, setCoverModalVisible] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [editForm] = Form.useForm();
  const [cloneForm] = Form.useForm();
  const [savingName, setSavingName] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadTabKey, setUploadTabKey] = useState('single');
  
  // 分页和搜索状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchValue, setSearchValue] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  
  const navigate = useNavigate();

  // 获取工作流列表
  const fetchWorkflows = useCallback(async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await workflowAPI.getAllWorkflows(page, pagination.pageSize, search);
      if (response.success) {
        setWorkflows(response.data.workflows || []);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.pagination?.total || 0
        }));
      } else {
        message.error('获取工作流列表失败');
      }
    } catch (error) {
      console.error('获取工作流列表失败:', error);
      message.error('获取工作流列表失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize]);

  // 组件加载时获取工作流列表
  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // 查看工作流详情
  const handleViewWorkflow = (workflowNum) => {
    navigate(`/workflow/${workflowNum}`);
  };

  // 打开编辑名称模态框
  const handleEditName = (record) => {
    setCurrentWorkflow(record);
    editForm.setFieldsValue({
      workflow_name: record.workflow_name
    });
    setEditModalVisible(true);
  };

  // 保存工作流名称
  const handleSaveName = async () => {
    try {
      const values = await editForm.validateFields();
      setSavingName(true);
      
      const response = await workflowAPI.updateWorkflowName(
        currentWorkflow.workflow_num,
        values.workflow_name
      );
      
      if (response.success) {
        message.success('工作流名称已更新');
        setEditModalVisible(false);
        // 更新本地数据
        setWorkflows(prevWorkflows => 
          prevWorkflows.map(workflow => 
            workflow.id === currentWorkflow.id 
              ? { ...workflow, workflow_name: values.workflow_name, updated_at: new Date().toISOString() }
              : workflow
          )
        );
      } else {
        message.error('更新工作流名称失败');
      }
    } catch (error) {
      console.error('更新工作流名称失败:', error);
      message.error('更新工作流名称失败: ' + (error.message || '未知错误'));
    } finally {
      setSavingName(false);
    }
  };

  // 确认删除工作流
  const showDeleteConfirm = (record) => {
    confirm({
      title: '确认删除工作流',
      icon: <ExclamationCircleOutlined />,
      content: `你确定要删除工作流"${record.workflow_name}"吗？此操作不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await workflowAPI.deleteWorkflow(record.workflow_num);
          
          if (response.success) {
            message.success('工作流已删除');
            // 更新本地数据
            setWorkflows(prevWorkflows => 
              prevWorkflows.filter(workflow => workflow.id !== record.id)
            );
          } else {
            message.error('删除工作流失败');
          }
        } catch (error) {
          console.error('删除工作流失败:', error);
          message.error('删除工作流失败: ' + (error.message || '未知错误'));
        }
      }
    });
  };

  // 打开复制工作流模态框
  const handleCloneWorkflow = (record) => {
    setCurrentWorkflow(record);
    cloneForm.setFieldsValue({
      source_workflow_num: record.workflow_num,
      source_workflow_name: record.workflow_name,
      workflow_num: '',
      workflow_name: `${record.workflow_name} 的副本`
    });
    setCloneModalVisible(true);
  };

  // 复制工作流
  const handleCloneSubmit = async () => {
    try {
      const values = await cloneForm.validateFields();
      setCloning(true);
      
      const response = await workflowAPI.cloneWorkflow(
        currentWorkflow.workflow_num,
        values.workflow_num,
        values.workflow_name
      );
      
      if (response.success) {
        message.success('工作流复制成功');
        setCloneModalVisible(false);
        
        // 更新本地数据
        fetchWorkflows();
      } else {
        message.error('复制工作流失败: ' + response.message);
      }
    } catch (error) {
      console.error('复制工作流失败:', error);
      message.error('复制工作流失败: ' + (error.message || '未知错误'));
    } finally {
      setCloning(false);
    }
  };

  // 打开上传模态框
  const showUploadModal = () => {
    setUploadModalVisible(true);
  };

  // 处理上传完成
  const handleUploadSuccess = () => {
    setUploadModalVisible(false);
    fetchWorkflows();
  };

  // 更新小程序展示状态
  const handleUpdateMiniprogramDisplay = async (workflowNum, checked) => {
    try {
      const response = await workflowAPI.updateMiniprogramDisplay(
        workflowNum,
        checked ? 1 : 0
      );
      
      if (response.success) {
        message.success(`已${checked ? '启用' : '禁用'}小程序端展示`);
        // 更新本地数据
        setWorkflows(prevWorkflows => 
          prevWorkflows.map(workflow => 
            workflow.workflow_num === workflowNum 
              ? { ...workflow, miniprogram_display: checked ? 1 : 0, updated_at: new Date().toISOString() }
              : workflow
          )
        );
      } else {
        message.error('更新小程序展示状态失败: ' + response.message);
      }
    } catch (error) {
      console.error('更新小程序展示状态失败:', error);
      message.error('更新小程序展示状态失败: ' + (error.message || '未知错误'));
    }
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchValue(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchWorkflows(1, value);
  };

  // 处理分页变化
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
    fetchWorkflows(page, searchValue);
  };

  // 打开封面图上传模态框
  const handleUploadCover = (record) => {
    setCurrentWorkflow(record);
    setCoverModalVisible(true);
  };

  // 处理封面图上传
  const handleCoverUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setCoverUploading(true);
      return;
    }
    
    if (info.file.status === 'done') {
      try {
        const response = await workflowAPI.uploadWorkflowCover(
          currentWorkflow.workflow_num, 
          info.file.originFileObj
        );
        
        if (response.success) {
          message.success('封面图上传成功');
          setCoverModalVisible(false);
          // 更新本地数据
          setWorkflows(prevWorkflows => 
            prevWorkflows.map(workflow => 
              workflow.workflow_num === currentWorkflow.workflow_num 
                ? { ...workflow, cover_image: response.data.cover_image }
                : workflow
            )
          );
        } else {
          message.error('封面图上传失败');
        }
      } catch (error) {
        console.error('封面图上传失败:', error);
        message.error('封面图上传失败: ' + (error.message || '未知错误'));
      } finally {
        setCoverUploading(false);
      }
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '封面图',
      dataIndex: 'cover_image',
      key: 'cover_image',
      width: 80,
      render: (coverImage) => (
        <Avatar 
          size={64} 
          src={coverImage} 
          icon={<EyeOutlined />}
          shape="square"
        />
      ),
    },
    {
      title: '工作流编号',
      dataIndex: 'workflow_num',
      key: 'workflow_num',
    },
    {
      title: '工作流名称',
      dataIndex: 'workflow_name',
      key: 'workflow_name',
    },
    {
      title: '小程序展示',
      dataIndex: 'miniprogram_display',
      key: 'miniprogram_display',
      width: 120,
      render: (miniprogramDisplay, record) => (
        <Space>
          <Switch
            checked={miniprogramDisplay === 1}
            onChange={(checked) => handleUpdateMiniprogramDisplay(record.workflow_num, checked)}
            size="small"
          />
          <Tag color={miniprogramDisplay === 1 ? 'success' : 'default'}>
            {miniprogramDisplay === 1 ? '展示' : '隐藏'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle" wrap>
          <Button 
            type="primary" 
            onClick={() => handleViewWorkflow(record.workflow_num)}
          >
            查看/编辑
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditName(record)}
          >
            修改名称
          </Button>
          <Button
            icon={<CameraOutlined />}
            onClick={() => handleUploadCover(record)}
          >
            上传封面
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={() => handleCloneWorkflow(record)}
          >
            基于此模板新建
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center' }}>
        <Title level={2}>工作流列表</Title>
        <Space>
          <Search
            placeholder="搜索工作流编号或名称"
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Button 
            type="primary" 
            icon={<UploadOutlined />}
            onClick={showUploadModal}
          >
            上传工作流
          </Button>
        </Space>
      </div>
      
      <Table
        dataSource={workflows}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: 1200 }}
      />
      
      {/* 自定义分页 */}
      {pagination.total > 0 && (
        <div style={{ marginTop: '16px', textAlign: 'right' }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
          />
        </div>
      )}

      {/* 封面图上传模态框 */}
      <Modal
        title="上传工作流封面图"
        open={coverModalVisible}
        onCancel={() => setCoverModalVisible(false)}
        footer={null}
      >
        {currentWorkflow && (
          <div>
            <Text strong>工作流编号：</Text>{currentWorkflow.workflow_num}
            <br />
            <Text strong>工作流名称：</Text>{currentWorkflow.workflow_name}
            <br /><br />
            
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('只能上传图片文件!');
                  return false;
                }
                const isLt5M = file.size / 1024 / 1024 < 5;
                if (!isLt5M) {
                  message.error('图片大小不能超过5MB!');
                  return false;
                }
                
                // 直接上传
                handleCoverUpload({ file: { status: 'done', originFileObj: file } });
                return false;
              }}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={coverUploading}
                size="large"
                block
              >
                {coverUploading ? '上传中...' : '选择封面图片'}
              </Button>
            </Upload>
            
            {currentWorkflow.cover_image && (
              <div style={{ marginTop: '16px' }}>
                <Text strong>当前封面图：</Text>
                <br />
                <Image
                  src={currentWorkflow.cover_image}
                  alt="工作流封面"
                  style={{ marginTop: '8px', maxWidth: '100%', maxHeight: '200px' }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
      
      {/* 编辑名称模态框 */}
      <Modal
        title="编辑工作流名称"
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            loading={savingName}
            onClick={handleSaveName}
          >
            保存
          </Button>
        ]}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            name="workflow_name"
            label="工作流名称"
            rules={[{ required: true, message: '请输入工作流名称' }]}
          >
            <Input placeholder="请输入工作流名称" />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 复制工作流模态框 */}
      <Modal
        title="复制工作流"
        visible={cloneModalVisible}
        onCancel={() => setCloneModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setCloneModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="clone" 
            type="primary" 
            loading={cloning}
            onClick={handleCloneSubmit}
          >
            复制
          </Button>
        ]}
      >
        <Form
          form={cloneForm}
          layout="vertical"
        >
          <Form.Item
            label="源工作流编号"
            name="source_workflow_num"
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            label="源工作流名称"
            name="source_workflow_name"
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="workflow_num"
            label="新工作流编号"
            rules={[{ required: true, message: '请输入新工作流编号' }]}
          >
            <Input placeholder="请输入新工作流编号" />
          </Form.Item>
          <Form.Item
            name="workflow_name"
            label="新工作流名称"
            rules={[{ required: true, message: '请输入新工作流名称' }]}
          >
            <Input placeholder="请输入新工作流名称" />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 上传工作流模态框 */}
      <Modal
        title="上传工作流"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={700}
      >
        <Tabs 
          activeKey={uploadTabKey} 
          onChange={setUploadTabKey}
          items={[
            {
              key: 'single',
              label: '单个上传',
              children: <WorkflowUploader onUploadSuccess={handleUploadSuccess} />
            },
            {
              key: 'batch',
              label: '批量上传',
              children: <WorkflowBatchUploader onUploadSuccess={handleUploadSuccess} />
            }
          ]}
        />
      </Modal>

    </div>
  );
};

export default WorkflowList; 